/**
 * SpeechBubble — Liquid glass speech bubble (DOM-based, replaces PixiJS version).
 *
 * Positioned above the avatar with a tail pointer. Supports:
 * - Typewriter text animation
 * - Prompt cycling (idle teaser messages)
 * - Static messages ("Connecting...")
 * - Accept/dismiss action buttons (for VLM suggestions)
 * - Spring enter/exit animations
 */

import { createGlassPanel, createGlassButton, glassText } from '../glass'
import { springEnter, springExit, type SpringAnimation } from '../spring'
import { injectTokens } from '../tokens'

/** Typewriter speed (ms per character). */
const TYPE_SPEED_MS = 30
/** How long to display each message before cycling. */
const HOLD_MS = 3000
/** Pause between messages during cycling. */
const CYCLE_PAUSE_MS = 500

export class GlassSpeechBubble {
  readonly el: HTMLDivElement
  private textEl: HTMLSpanElement
  private actionsEl: HTMLDivElement
  private tailEl: HTMLDivElement

  private prompts: string[] = []
  private promptIndex = 0
  private typeTimer: ReturnType<typeof setTimeout> | null = null
  private holdTimer: ReturnType<typeof setTimeout> | null = null
  private cycling = false
  private currentAnim: SpringAnimation | null = null

  // Callbacks
  onClick: (() => void) | null = null
  onAccept: (() => void) | null = null
  onDismiss: (() => void) | null = null

  constructor(prompts?: string[]) {
    injectTokens()
    this.prompts = prompts ?? [
      'Need help finding something?',
      'I can walk you through this page.',
      "Questions? I'm here to help.",
    ]

    // Main bubble panel
    this.el = createGlassPanel({
      radius: 'md',
      elevation: 'elevated',
      className: 'hs-speech-bubble',
    }) as HTMLDivElement

    Object.assign(this.el.style, {
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: '12px',
      padding: 'var(--hs-space-3) var(--hs-space-4)',
      maxWidth: '280px',
      minWidth: '120px',
      pointerEvents: 'auto',
      cursor: 'pointer',
      opacity: '0',
      display: 'none',
      zIndex: '4',
    })

    // Text content
    this.textEl = document.createElement('span')
    glassText(this.textEl, 'primary', 'base', 'normal')
    this.textEl.style.display = 'block'
    this.el.appendChild(this.textEl)

    // Action buttons container (hidden by default)
    this.actionsEl = document.createElement('div')
    Object.assign(this.actionsEl.style, {
      display: 'none',
      gap: 'var(--hs-space-2)',
      marginTop: 'var(--hs-space-3)',
      justifyContent: 'flex-end',
    })
    this.el.appendChild(this.actionsEl)

    // Tail (downward pointing triangle)
    this.tailEl = document.createElement('div')
    Object.assign(this.tailEl.style, {
      position: 'absolute',
      bottom: '-6px',
      left: '50%',
      transform: 'translateX(-50%) rotate(45deg)',
      width: '12px',
      height: '12px',
      background: 'var(--hs-glass-bg-elevated)',
      backdropFilter: 'blur(var(--hs-glass-blur)) saturate(var(--hs-glass-saturate))',
      WebkitBackdropFilter: 'blur(var(--hs-glass-blur)) saturate(var(--hs-glass-saturate))',
      borderRight: '1px solid var(--hs-glass-border)',
      borderBottom: '1px solid var(--hs-glass-border)',
      zIndex: '-1',
    })
    this.el.appendChild(this.tailEl)

    // Click on bubble → start conversation
    this.el.addEventListener('click', (e) => {
      e.stopPropagation()
      this.onClick?.()
    })
  }

  // -----------------------------------------------------------------------
  // Prompt cycling (teaser mode)
  // -----------------------------------------------------------------------

  /** Start cycling through prompts with typewriter animation. */
  start(): void {
    if (this.cycling) return
    this.cycling = true
    this.promptIndex = 0
    this.showNextPrompt()
  }

  /** Stop cycling and hide the bubble. */
  stop(): void {
    this.cycling = false
    this.clearTimers()
    this.hide()
  }

  private showNextPrompt(): void {
    if (!this.cycling) return

    const text = this.prompts[this.promptIndex % this.prompts.length]
    this.promptIndex++

    this.actionsEl.style.display = 'none'
    this.typewrite(text, () => {
      this.holdTimer = setTimeout(() => {
        this.fadeOutAndNext()
      }, HOLD_MS)
    })
  }

  private fadeOutAndNext(): void {
    this.currentAnim = springExit(this.el, {
      remove: false,
      preset: 'smooth',
      onComplete: () => {
        this.el.style.display = 'none'
        setTimeout(() => {
          if (this.cycling) this.showNextPrompt()
        }, CYCLE_PAUSE_MS)
      },
    })
  }

  // -----------------------------------------------------------------------
  // Typewriter
  // -----------------------------------------------------------------------

  private typewrite(text: string, onDone?: () => void): void {
    this.clearTimers()
    this.textEl.textContent = ''

    // Show the bubble
    this.el.style.display = 'block'
    this.currentAnim = springEnter(this.el, { preset: 'snappy' })

    let i = 0
    const tick = () => {
      if (i < text.length) {
        this.textEl.textContent = text.slice(0, ++i)
        this.typeTimer = setTimeout(tick, TYPE_SPEED_MS)
      } else {
        onDone?.()
      }
    }
    this.typeTimer = setTimeout(tick, TYPE_SPEED_MS)
  }

  // -----------------------------------------------------------------------
  // Static messages (e.g. "Connecting...")
  // -----------------------------------------------------------------------

  /** Show a static message (no cycling, no typewriter). */
  showStatic(text: string): void {
    this.clearTimers()
    this.cycling = false
    this.actionsEl.style.display = 'none'
    this.textEl.textContent = text

    this.el.style.display = 'block'
    this.currentAnim = springEnter(this.el, { preset: 'snappy' })
  }

  /** Hide a static message. */
  hideStatic(): void {
    this.hide()
  }

  // -----------------------------------------------------------------------
  // Suggestion messages (with accept/dismiss buttons)
  // -----------------------------------------------------------------------

  /** Show a VLM suggestion with action buttons. */
  showSuggestion(message: string): void {
    this.clearTimers()
    this.cycling = false
    this.textEl.textContent = ''

    // Build action buttons
    this.actionsEl.innerHTML = ''
    const acceptBtn = createGlassButton({
      label: 'Sure',
      variant: 'accent',
      size: 'sm',
      icon: '\u2713',
      onClick: () => {
        this.onAccept?.()
        this.hide()
      },
    })
    const dismissBtn = createGlassButton({
      label: 'Not now',
      variant: 'ghost',
      size: 'sm',
      icon: '\u2715',
      onClick: () => {
        this.onDismiss?.()
        this.hide()
      },
    })
    this.actionsEl.appendChild(acceptBtn)
    this.actionsEl.appendChild(dismissBtn)
    this.actionsEl.style.display = 'flex'

    // Typewrite the message
    this.el.style.display = 'block'
    this.currentAnim = springEnter(this.el, { preset: 'snappy' })

    let i = 0
    const tick = () => {
      if (i < message.length) {
        this.textEl.textContent = message.slice(0, ++i)
        this.typeTimer = setTimeout(tick, TYPE_SPEED_MS)
      }
    }
    this.typeTimer = setTimeout(tick, TYPE_SPEED_MS)
  }

  // -----------------------------------------------------------------------
  // Visibility
  // -----------------------------------------------------------------------

  private hide(): void {
    this.currentAnim?.stop()
    if (this.el.style.display === 'none') return

    this.currentAnim = springExit(this.el, {
      remove: false,
      preset: 'smooth',
      onComplete: () => {
        this.el.style.display = 'none'
      },
    })
  }

  private clearTimers(): void {
    if (this.typeTimer) { clearTimeout(this.typeTimer); this.typeTimer = null }
    if (this.holdTimer) { clearTimeout(this.holdTimer); this.holdTimer = null }
  }

  destroy(): void {
    this.currentAnim?.stop()
    this.clearTimers()
    this.el.remove()
  }
}
